/**
 * Kimlik Havuzu client API — server authoritative yazım; UI maskeli gösterir.
 */
import { apiUrl } from './apiBase';
import { auth, db } from '../firebase';
import { KNOWN_BRANCHES } from './invoiceSchema';
import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  where
} from 'firebase/firestore';
import {
  resolveCounterpartyTcknFromBankMovement,
  resolveCounterpartyVknFromBankMovement
} from './bankMovementTaxId';

const getActivePersonel = () => {
  try {
    return JSON.parse(localStorage.getItem('aktifPersonel') || 'null');
  } catch {
    return null;
  }
};

async function postIdentity(path, body) {
  const headers = { 'Content-Type': 'application/json' };
  const sharedSecret = String(process.env.REACT_APP_CAMPAIGN_AI_SHARED_SECRET || '').trim();
  if (sharedSecret) headers['x-ukba-internal-key'] = sharedSecret;
  try {
    const token = await auth.currentUser?.getIdToken?.();
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch (_) {
    /* ignore */
  }
  const res = await fetch(apiUrl(path), {
    method: 'POST',
    headers,
    body: JSON.stringify({ ...(body || {}), personel: getActivePersonel() })
  });
  let data = {};
  try {
    data = await res.json();
  } catch (_) {
    data = {};
  }
  if (!res.ok || data?.ok === false || data?.success === false) {
    const err = new Error(data?.message || data?.error || `İstek başarısız (${res.status || '?'})`);
    err.code = data?.code;
    err.payload = data;
    throw err;
  }
  return data;
}

export const fetchIdentitySettings = () => postIdentity('/api/identity/settings', {});
export const saveIdentitySettingsApi = (settings) =>
  postIdentity('/api/identity/settings', { settings });
export const runIdentityBackfillApi = (payload) => postIdentity('/api/identity/backfill', payload || {});
export const listIdentityPoolApi = (sube, limitCount = 100) =>
  postIdentity('/api/identity/list', { sube, limit: limitCount });
export const getIdentityApi = (sube, identityId, reveal = false) =>
  postIdentity('/api/identity/get', { sube, identityId, reveal });
export const listRiskReviewsApi = (sube, limitCount = 80) =>
  postIdentity('/api/risk/list', { sube, limit: limitCount });
export const manualPatchIdentityApi = (payload) => postIdentity('/api/identity/manual-patch', payload);

/**
 * Fatura taslağı için havuz çözümleme.
 * usable ise identityId üzerinden Firestore'dan TC/VKN okunur (UI maskeler; draft doldurma).
 */
export async function resolveIdentityFromPoolForDraft({ sube, customerId, bankMovement, customerName }) {
  if (!sube) return { usable: false };
  try {
    const data = await postIdentity('/api/identity/resolve', {
      sube,
      customerId: customerId || null,
      movement: bankMovement || null
    });
    if (!data?.usable || !data?.identity?.id) {
      return { usable: false, reason: data?.reason || 'not_usable' };
    }

    let tckn = resolveCounterpartyTcknFromBankMovement(bankMovement) || '';
    let vkn = resolveCounterpartyVknFromBankMovement(bankMovement) || '';

    if (!tckn && !vkn) {
      const snap = await getDoc(doc(db, 'subeler', sube, 'kimlikHavuzu', data.identity.id));
      if (snap.exists()) {
        const row = snap.data() || {};
        tckn = String(row.tckn || '').replace(/\D/g, '');
        vkn = String(row.vkn || '').replace(/\D/g, '');
        if (tckn.length !== 11) tckn = '';
        if (vkn.length !== 10) vkn = '';
      }
    }

    void customerName;
    if (!tckn && !vkn) return { usable: false, reason: 'no_tax_digits' };
    return {
      usable: true,
      tckn,
      vkn,
      identityId: data.identity.id,
      confidence: data.identity.confidence
    };
  } catch (_) {
    return { usable: false, reason: 'api_unavailable' };
  }
}

/**
 * Kimlik havuzu — TÜM şubeler birleşik.
 *
 * Fatura eşleştirmesi şube bazlıdır (şubeler karışmasın), ama risk değerlendirmesi
 * öyle değil: aynı kişi Kavaklı'da ve Sefaköy'de işlem yapmış olabilir ve MASAK
 * değerlendirmesi kişinin tüm hareketine bakmalıdır. Bu yüzden havuz şubeden
 * bağımsız okunur; her kayıt hangi şubeden geldiğini `sube` alanında taşır.
 */
/**
 * Kolaysoft fatura dökümünden gelen, şubesi bilinmeyen kişiler ayrı bir havuz
 * bölümünde (subeler/kolaysoft/kimlikHavuzu) durur. Banka kayıtlarıyla karışmaz;
 * okunurken diğer şubelerle birleştirilir. Bkz. scripts/kolaysoft-identity-pool.cjs
 */
export const KOLAYSOFT_POOL_BUCKET = 'kolaysoft';

export const IDENTITY_POOL_BUCKETS = [...KNOWN_BRANCHES.map((b) => b.key), KOLAYSOFT_POOL_BUCKET];

const poolTaxKey = (row) => String(row?.tckn || row?.vkn || '').replace(/\D/g, '');

/** lastSeenAt hem Firestore zaman damgası hem ISO metin olabiliyor. */
const seenMs = (value) => {
  if (!value) return 0;
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : 0;
};

/**
 * Aynı TCKN hem şube havuzunda hem Kolaysoft bölümünde olabilir (ör. banka kaydı
 * yüklemeden sonra oluştu). Listede bir kez görünsün; banka kaydı önceliklidir.
 */
export const mergeIdentityPoolRows = (rows = []) => {
  const byTax = new Map();
  const out = [];
  rows.forEach((row) => {
    const tax = poolTaxKey(row);
    if (!tax) {
      out.push(row);
      return;
    }
    const prev = byTax.get(tax);
    if (!prev) {
      byTax.set(tax, row);
      return;
    }
    if (prev.sube === KOLAYSOFT_POOL_BUCKET && row.sube !== KOLAYSOFT_POOL_BUCKET) {
      byTax.set(tax, { ...row, kolaysoft: row.kolaysoft || prev.kolaysoft });
    }
  });
  return [...out, ...byTax.values()].sort((a, b) => seenMs(b.lastSeenAt) - seenMs(a.lastSeenAt));
};

/** Havuzdaki gerçek kayıt sayısı (liste en son kayıtlarla sınırlı olduğu için ayrıca sayılır). */
export async function countIdentityPool() {
  const counts = await Promise.all(
    IDENTITY_POOL_BUCKETS.map((bucket) =>
      getCountFromServer(collection(db, 'subeler', bucket, 'kimlikHavuzu'))
        .then((snap) => snap.data().count)
        .catch(() => 0)
    )
  );
  const perBucket = Object.fromEntries(IDENTITY_POOL_BUCKETS.map((b, i) => [b, counts[i]]));
  return { total: counts.reduce((a, b) => a + b, 0), perBucket };
}

const foldSearch = (s) =>
  String(s || '')
    .replace(/[ıİ]/g, 'i')
    .replace(/[şŞ]/g, 's')
    .replace(/[ğĞ]/g, 'g')
    .replace(/[üÜ]/g, 'u')
    .replace(/[öÖ]/g, 'o')
    .replace(/[çÇ]/g, 'c')
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Havuzda arama — tüm şubeler + Kolaysoft bölümü.
 *  - 10/11 haneli sayı → tckn / vkn eşitliği
 *  - isim → searchTokens array-contains (en uzun kelime), kalan kelimeler burada süzülür
 */
export async function searchIdentityPool(text) {
  const raw = String(text || '').trim();
  if (!raw) return [];
  const digits = raw.replace(/\D/g, '');
  const isTax = /^\d[\d\s]*$/.test(raw) && (digits.length === 10 || digits.length === 11);
  const words = foldSearch(raw).split(' ').filter((w) => w.length >= 2);
  if (!isTax && !words.length) return [];
  const longest = [...words].sort((a, b) => b.length - a.length)[0];

  const perBucket = await Promise.all(
    IDENTITY_POOL_BUCKETS.map(async (bucket) => {
      const col = collection(db, 'subeler', bucket, 'kimlikHavuzu');
      const q = isTax
        ? query(col, where(digits.length === 11 ? 'tckn' : 'vkn', '==', digits), limit(20))
        : query(col, where('searchTokens', 'array-contains', longest), limit(200));
      try {
        const snap = await getDocs(q);
        return snap.docs.map((d) => ({ id: d.id, sube: bucket, ...(d.data() || {}) }));
      } catch (_) {
        return [];
      }
    })
  );
  let rows = perBucket.flat();
  if (!isTax && words.length > 1) {
    rows = rows.filter((r) => {
      const hay = foldSearch(
        [r.customerName, r.normalizedName, ...(r.nameHistory || []).map((h) => h?.name)].join(' ')
      );
      return words.every((w) => hay.includes(w));
    });
  }
  return mergeIdentityPoolRows(rows);
}

export function subscribeIdentityPool(sube, onData, onError) {
  const branches = IDENTITY_POOL_BUCKETS;
  const perBranch = new Map();
  let ready = 0;

  const emit = () => {
    // Tüm abonelikler ilk veriyi verene kadar kısmi liste basma
    if (ready < branches.length) return;
    const merged = [];
    branches.forEach((key) => {
      (perBranch.get(key) || []).forEach((row) => merged.push(row));
    });
    // lastSeenAt metin (ISO) saklandığı için eskiden sıralama hiç çalışmıyordu (hepsi 0).
    onData(mergeIdentityPoolRows(merged).slice(0, 600));
  };

  const unsubs = branches.map((branchKey) => {
    const q = query(
      collection(db, 'subeler', branchKey, 'kimlikHavuzu'),
      orderBy('lastSeenAt', 'desc'),
      limit(200)
    );
    let counted = false;
    return onSnapshot(
      q,
      (snap) => {
        perBranch.set(
          branchKey,
          snap.docs.map((d) => ({ id: d.id, sube: branchKey, ...(d.data() || {}) }))
        );
        if (!counted) {
          counted = true;
          ready += 1;
        }
        emit();
      },
      (err) => {
        // Bir şube okunamazsa diğerleri gösterilmeye devam eder
        perBranch.set(branchKey, []);
        if (!counted) {
          counted = true;
          ready += 1;
        }
        emit();
        onError?.(err);
      }
    );
  });

  return () => unsubs.forEach((u) => u?.());
}

export function subscribeRiskReviews(sube, onData, onError) {
  if (!sube) return () => {};
  const q = query(
    collection(db, 'subeler', sube, 'riskReviews'),
    orderBy('createdAt', 'desc'),
    limit(80)
  );
  return onSnapshot(
    q,
    (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }))),
    (err) => onError?.(err)
  );
}

export const DEFAULT_IDENTITY_SETTINGS = {
  identityPoolEnabled: true,
  autoLearnBankIdentity: true,
  ibanCustomerMatching: true,
  identityConflictAlerts: true,
  transactionRiskEnabled: true,
  transactionRiskMode: 'monitor',
  highAmountThreshold: 250000,
  frequency24hCount: 8,
  frequency7dCount: 25,
  frequency30dCount: 60,
  splitDetectionEnabled: true,
  splitWindowMinutes: 120,
  splitMinParts: 3,
  splitAmountToleranceTl: 1,
  riskScoreNormalMax: 29,
  riskScoreLowMax: 59,
  riskScoreReviewMax: 79
};

export const maskTaxIdClient = (taxId) => {
  const d = String(taxId || '').replace(/\D/g, '');
  if (d.length === 11) return `${d.slice(0, 3)}****${d.slice(-2)}`;
  if (d.length === 10) return `${d.slice(0, 2)}****${d.slice(-2)}`;
  return d ? '****' : '—';
};

export const maskIbanClient = (iban) => {
  const n = String(iban || '').replace(/\s+/g, '').toUpperCase();
  if (n.length < 10) return n || '—';
  return `${n.slice(0, 4)}…${n.slice(-4)}`;
};
